import { useState } from 'react'

import { type Task, asDay, isDone, timeLeft, todayAsDay } from '../../api/tasks'
import { Button } from '../../ui/Control'

/**
 * One open point, opened.
 *
 * Everything a task has that does not fit on its line in the list: **the day it is due**, set and
 * removed here, **what state it is in**, and **deleting it**. It is deliberately not a form — there is
 * no "save" and nothing to submit. A day chosen is a day sent; the tick is a tick.
 *
 * This is also the room the area grows into. Linking a task to a subject, to a lecture or to a document
 * is issue 15's business and lands here, on a surface that already exists, rather than making the list
 * itself heavier.
 *
 * On a phone this surface takes the whole frame and the list steps back — `Lanes` does that, and the
 * way back is the frame's own back control. Nothing here names a device.
 */
export interface OpenTaskProps {
  readonly task: Task
  readonly onChangeDue: (dueOn: string | null) => void
  readonly onTickOff: (done: boolean) => void
  readonly onRemove: () => void
  readonly onClose: () => void
}

export function OpenTask({ task, onChangeDue, onTickOff, onRemove, onClose }: OpenTaskProps) {
  /*
   * What is in the date field, held here so that the field does not flick back to the old day while
   * the change is on its way.
   *
   * Seeded once and never synchronised afterwards: the list keys this surface by what the service
   * last said about the task, so a day set anywhere arrives as a new surface with the new day in it.
   */
  const [dueOn, setDueOn] = useState(task.dueOn ?? '')

  return (
    <article
      className="sheet sheet--task"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onClose()
        }
      }}
    >
      <header className="sheet__head">
        <Button
          tone="bare"
          className="sheet__close"
          icon="close"
          iconOnlyLabel="Close this task"
          onClick={onClose}
        />
        <h2 className="sheet__title-text">{task.text}</h2>
        <p className="sheet__facts">
          <span>{isDone(task) ? 'Ticked off' : 'Open'}</span>
          {task.dueOn !== null && (
            <>
              <span aria-hidden="true">·</span>
              <span>due {asDay(task.dueOn)}</span>
              <span aria-hidden="true">·</span>
              <span>{timeLeft(task)}</span>
            </>
          )}
        </p>
      </header>

      <div className="task-detail">
        <div className="task-detail__due">
          <label className="task-detail__label" htmlFor="task-due">
            Due
          </label>
          {/*
            The browser's own date field: it is the one control that is a date picker on a phone, a
            keyboard-typed date on a Mac, and understood by a screen reader without anything being
            explained to it. A calendar of our own would be worse in all three places.
          */}
          <input
            id="task-due"
            type="date"
            className="task-detail__date"
            value={dueOn}
            onChange={(event) => {
              const wanted = event.target.value
              setDueOn(wanted)
              onChangeDue(wanted === '' ? null : wanted)
            }}
          />
          <div className="controls controls--tight">
            {/* The two days somebody actually means, without opening a calendar for either. */}
            <Button
              tone="quiet"
              onClick={() => {
                onChangeDue(todayAsDay())
              }}
            >
              Today
            </Button>
            <Button
              tone="quiet"
              onClick={() => {
                onChangeDue(todayAsDay(inDays(1)))
              }}
            >
              Tomorrow
            </Button>
            {task.dueOn !== null && (
              /*
                Taking the day away is an ordinary thing to do rather than an erasure: a task with no
                day is the one that never becomes urgent by itself, which is why it stands at the top
                of the list rather than at the bottom.
              */
              <Button
                tone="bare"
                onClick={() => {
                  onChangeDue(null)
                }}
              >
                No date
              </Button>
            )}
          </div>
          <p className="task-detail__aside">
            A task without a date is not a task without importance. It stands at the top of the
            list, because it is the one nothing else will ever remind you of.
          </p>
        </div>

        <div className="controls">
          <Button
            tone="primary"
            onClick={() => {
              onTickOff(!isDone(task))
            }}
          >
            {isDone(task) ? 'Not done after all' : 'Tick off'}
          </Button>
          {/*
            Immediate, and nothing is asked. A task is a sentence and is typed again in five seconds,
            and it already has a way back — the tick. Two ways back for one small thing would be two
            nobody can tell apart.
          */}
          <Button tone="quiet" className="control--danger" onClick={onRemove}>
            Delete
          </Button>
        </div>

        {isDone(task) && (
          <p className="task-detail__aside">
            Ticked off tasks stay here for a month and then go on their own. Nothing ever removes
            one that is still open.
          </p>
        )}
      </div>
    </article>
  )
}

/** A date this many days from now, for the two shortcuts above. */
function inDays(days: number): Date {
  const at = new Date()
  at.setDate(at.getDate() + days)
  return at
}
